import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { ShieldCheck, Star, Sparkles, MapPin, Wallet, Navigation } from "lucide-react";

const HERO = "https://static.prod-images.emergentagent.com/jobs/ef87128c-9ad4-43cd-b243-b50370870ad7/images/e6469ef9846140998a00daae4420ec6218af30f11ec9bd4b0d63752330fc8336.jpeg";

const features = [
  { icon: ShieldCheck, title: "Verified & Trusted", desc: "ID and phone verification with a transparent trust score on every profile." },
  { icon: Wallet, title: "Pay how you want, securely", desc: "Pay securely through Stripe or with cash once your errand is done — whatever works for you." },
  { icon: Navigation, title: "Live Runner Tracking", desc: "Follow your runner on a live map with distance and ETA, right up to your door." },
  { icon: Star, title: "Ratings & Reviews", desc: "Two-way reviews keep the whole community accountable and kind." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-2xl bg-primary flex items-center justify-center text-white"><Sparkles size={18} /></span>
          <span className="font-heading font-extrabold text-xl tracking-tight">Errandell</span>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/login"><Button data-testid="landing-login-btn" variant="ghost" className="rounded-full font-semibold">Log in</Button></Link>
          <Link to="/register"><Button data-testid="landing-signup-btn" className="rounded-full font-bold bg-primary hover:bg-[#FF8D70] active:scale-95 transition-transform">Get started</Button></Link>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-5 pt-10 pb-16 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <span className="inline-flex items-center gap-2 bg-accent/15 text-accent font-bold text-xs px-4 py-2 rounded-full mb-6">
            <MapPin size={14} /> For students & neighbors
          </span>
          <h1 className="font-heading font-black text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[#1A1D20] mb-5">
            Local errands,<br />by <span className="text-primary">Local neighbors.</span>
          </h1>
          <p className="text-base sm:text-lg text-[#6B7280] max-w-md mb-8">
            Get matched with a trusted errand runner from your local neighborhood, and pay securely when it's done.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/register"><Button data-testid="hero-cta-btn" size="lg" className="rounded-full font-bold text-base px-8 h-13 py-6 bg-primary hover:bg-[#FF8D70] active:scale-95 transition-transform">Post your first errand</Button></Link>
            <Link to="/login"><Button data-testid="hero-browse-btn" size="lg" variant="outline" className="rounded-full font-bold text-base px-8 py-6 border-2">Browse errands</Button></Link>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.1 }} className="relative">
          <div className="absolute -top-6 -left-6 w-24 h-24 bg-accent/20 rounded-3xl -z-0 hidden sm:block" />
          <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-secondary/15 rounded-full -z-0 hidden sm:block" />
          <img src={HERO} alt="Eucalyptus trees along Palos Verdes Drive North in Valmonte" className="relative rounded-3xl w-full h-[440px] object-cover shadow-lift" />
          <div className="absolute bottom-5 left-5 bg-white/90 backdrop-blur-md rounded-2xl px-4 py-3 shadow-soft flex items-center gap-2">
            <ShieldCheck className="text-[#66C2A5]" size={20} />
            <span className="text-sm font-bold text-[#1A1D20]">Verified community</span>
          </div>
        </motion.div>
      </section>

      <section className="max-w-6xl mx-auto px-5 py-12">
        <h2 className="font-heading font-extrabold text-3xl text-center text-[#1A1D20] mb-2">Safe by design</h2>
        <p className="text-center text-[#6B7280] mb-10">Everything you need to help — and be helped — with confidence.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div key={f.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="bg-white rounded-3xl p-6 border border-gray-100 shadow-soft">
                <span className="w-11 h-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-4"><Icon size={22} /></span>
                <h3 className="font-heading font-extrabold text-lg text-[#1A1D20] mb-1">{f.title}</h3>
                <p className="text-sm text-[#6B7280]">{f.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      <footer className="max-w-6xl mx-auto px-5 py-10 text-center text-sm text-[#6B7280]">
        <Link to="/terms" data-testid="footer-terms-link" className="font-semibold text-[#4A5361] hover:text-primary hover:underline">Terms & Privacy Policy</Link>
        <span className="mx-2">·</span>
        © 2026 Errandell — built for community.
      </footer>
    </div>
  );
}
